import {
  APP_CURRENCY,
  APP_CURRENCY_LOCALE,
  formatCalendarDate as formatCalendarDateInLocale,
} from '@trellis/shared'

export function formatDate(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  locale = APP_CURRENCY_LOCALE,
): string {
  return new Intl.DateTimeFormat(locale, options).format(
    typeof date === 'string' ? new Date(date) : date,
  )
}

/**
 * Formats a date-only field (`issueDate`, `dueAt`, `expiresAt`) stored as midnight UTC.
 * Unlike {@link formatDate}, never shifts into the browser's timezone — a browser behind
 * UTC would otherwise show the previous day.
 */
export function formatCalendarDate(
  date: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  locale = APP_CURRENCY_LOCALE,
): string {
  return formatCalendarDateInLocale(date, locale, options)
}

/**
 * Today's local calendar date as a yyyy-MM-dd string, matching how forms store date field
 * state.
 */
export function todayIsoDate(): string {
  return new Intl.DateTimeFormat('en-CA').format(new Date())
}

/** Current local time as an HH:mm string, matching `<input type="time">` value format. */
export function nowTime(): string {
  return new Date().toTimeString().slice(0, 5)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const currencyFormatters = new Map<string, Intl.NumberFormat>()

export function getCurrencyFormatter(
  currency = APP_CURRENCY,
  options: Intl.NumberFormatOptions = {},
  locale = APP_CURRENCY_LOCALE,
): Intl.NumberFormat {
  const cacheKey = JSON.stringify([locale, currency, options])
  let formatter = currencyFormatters.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { style: 'currency', currency, ...options })
    currencyFormatters.set(cacheKey, formatter)
  }
  return formatter
}

export function formatCurrency(
  amount: number,
  currency = APP_CURRENCY,
  locale = APP_CURRENCY_LOCALE,
): string {
  return getCurrencyFormatter(currency, {}, locale).format(amount)
}

export function formatCurrencyCompact(
  amount: number,
  currency = APP_CURRENCY,
  locale = APP_CURRENCY_LOCALE,
): string {
  return getCurrencyFormatter(
    currency,
    { notation: 'compact', maximumFractionDigits: 1 },
    locale,
  ).format(amount)
}

export function formatAmount(amount: number, locale = APP_CURRENCY_LOCALE): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}
